import { Box, Select, MenuItem, FormControl, useTheme } from "@mui/material";
import { MdSms, MdEmail } from "react-icons/md";
import { RequestMagicLinkMethod } from "../Enums/RequestMagicLinkMethod";
import { CupertinoSegmentPicker } from "./CupertinoSegmentPicker";

export type MagicLinkContactPickerProps = {
    magicLinkEmails: string[];
    magicLinkPhones: string[];
    magicLinkRecipient: string | null;
    setMagicLinkRecipient: (recipient: string | null) => void;
    magicLinkDeliveryMethod: RequestMagicLinkMethod | null;
    setMagicLinkDeliveryMethod: (method: RequestMagicLinkMethod | null) => void;
};

export const MagicLinkContactPicker = ({
    magicLinkEmails,
    magicLinkPhones,
    magicLinkRecipient,
    setMagicLinkRecipient,
    magicLinkDeliveryMethod,
    setMagicLinkDeliveryMethod,
}: MagicLinkContactPickerProps) => {
    const theme = useTheme();

    // Ensure we have a valid delivery method (default to SMS if null)
    const currentDeliveryMethod = magicLinkDeliveryMethod ?? RequestMagicLinkMethod.Sms;

    const handleDeliveryMethodChange = (method: RequestMagicLinkMethod) => {
        setMagicLinkDeliveryMethod(method);
        // Update recipient based on the new method
        if (method === RequestMagicLinkMethod.Sms && magicLinkPhones.length > 0) {
            setMagicLinkRecipient(magicLinkPhones[0]);
        } else if (method === RequestMagicLinkMethod.Email && magicLinkEmails.length > 0) {
            setMagicLinkRecipient(magicLinkEmails[0]);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <CupertinoSegmentPicker<RequestMagicLinkMethod>
                label="Výber spôsobu odoslania Magic Link"
                options={[
                    {
                        value: RequestMagicLinkMethod.Sms,
                        label: "Telefón",
                        icon: MdSms,
                        labelBelow: magicLinkPhones.length === 1 ? magicLinkPhones[0] : undefined,
                    },
                    {
                        value: RequestMagicLinkMethod.Email,
                        label: "Email",
                        icon: MdEmail,
                        labelBelow: magicLinkEmails.length === 1 ? magicLinkEmails[0] : undefined,
                    }
                ]}
                selectedValue={currentDeliveryMethod}
                onSelect={handleDeliveryMethodChange}
            />
            {currentDeliveryMethod === RequestMagicLinkMethod.Sms && magicLinkPhones.length > 1 && (
                <FormControl fullWidth sx={{ marginTop: 1 }}>
                    <Select
                        value={magicLinkRecipient || ""}
                        onChange={(e) => setMagicLinkRecipient(e.target.value)}
                        displayEmpty
                        MenuProps={{
                            slotProps: {
                                root: {
                                    sx: {
                                        zIndex: "9000 !important",
                                    },
                                },
                            },
                            PaperProps: {
                                sx: {
                                    zIndex: "9000 !important",
                                },
                            },
                        }}
                        sx={{
                            borderRadius: "7px",
                            backgroundColor: theme.palette.common.white,
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: theme.palette.divider,
                                borderWidth: "1px",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: theme.palette.divider,
                                borderWidth: "1px",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: theme.palette.primary.main,
                                borderWidth: "2px",
                            },
                        }}
                    >
                        {magicLinkPhones.map((phone, index) => (
                            <MenuItem key={index} value={phone}>
                                {phone}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            {currentDeliveryMethod === RequestMagicLinkMethod.Email && magicLinkEmails.length > 1 && (
                <FormControl fullWidth sx={{ marginTop: 1 }}>
                    <Select
                        value={magicLinkRecipient || ""}
                        onChange={(e) => setMagicLinkRecipient(e.target.value)}
                        displayEmpty
                        MenuProps={{
                            slotProps: {
                                root: {
                                    sx: {
                                        zIndex: "9000 !important",
                                    },
                                },
                            },
                            PaperProps: {
                                sx: {
                                    zIndex: "9000 !important",
                                },
                            },
                        }}
                        sx={{
                            borderRadius: "7px",
                            backgroundColor: theme.palette.common.white,
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: theme.palette.divider,
                                borderWidth: "1px",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: theme.palette.divider,
                                borderWidth: "1px",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: theme.palette.primary.main,
                                borderWidth: "2px",
                            },
                        }}
                    >
                        {magicLinkEmails.map((email, index) => (
                            <MenuItem key={index} value={email}>
                                {email}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </Box>
    );
};

